import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2 } from "lucide-react";
import { SEO } from "../components/SEO";
import { API, LOGO_MARK } from "../lib/site";

const ResetPassword = () => {
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);
    const navigate = useNavigate();

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (password.length < 8) {
            setError("Password must be at least 8 characters.");
            return;
        }
        if (password !== confirm) {
            setError("Passwords do not match.");
            return;
        }
        setBusy(true);
        try {
            const { data } = await axios.post(`${API}/password/reset`, { token, password });
            if (data.ok) {
                setDone(true);
            } else {
                setError(data.detail || "This reset link is invalid or has expired.");
            }
        } catch {
            setError("Could not reset your password. Please request a new link.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <main>
            <SEO title="Reset Password | Global Green Hotel" description="Choose a new password for your GGH account." />
            <div className="grain flex min-h-screen items-center justify-center bg-[#04150D] px-5 py-32">
                <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#07211A] p-9" data-testid="reset-page">
                    <img src={LOGO_MARK} alt="Global Green Hotel logo" className="w-36" />
                    {done ? (
                        <div data-testid="reset-done">
                            <CheckCircle2 size={36} className="mt-8 text-[#D9B96A]" aria-hidden="true" />
                            <h1 className="font-display mt-5 text-2xl font-medium text-[#FDFBF7]">Password updated</h1>
                            <p className="mt-3 text-sm leading-relaxed text-[#FDFBF7]/60">
                                Your password has been changed. Sign in with your new password.
                            </p>
                            <button
                                type="button"
                                data-testid="reset-goto-login"
                                onClick={() => navigate("/login")}
                                className="btn-gold mt-8 w-full"
                            >
                                Go to Login
                            </button>
                        </div>
                    ) : !token ? (
                        <div data-testid="reset-invalid">
                            <h1 className="font-display mt-7 text-2xl font-medium text-[#FDFBF7]">Invalid reset link</h1>
                            <p className="mt-3 text-sm text-[#FDFBF7]/60">
                                This link is missing its token. Please request a fresh reset link.
                            </p>
                            <Link to="/forgot-password" className="btn-outline-light mt-8 w-full">
                                Request New Link
                            </Link>
                        </div>
                    ) : (
                        <form onSubmit={submit} data-testid="reset-form">
                            <h1 className="font-display mt-7 text-3xl font-medium text-[#FDFBF7]">Choose a new password</h1>
                            <label htmlFor="rp-password" className="mt-8 mb-2 block text-xs font-extrabold uppercase tracking-[0.2em] text-[#FDFBF7]/70">
                                New Password
                            </label>
                            <input
                                id="rp-password"
                                data-testid="reset-password"
                                type="password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="input-field"
                                placeholder="At least 8 characters"
                                autoComplete="new-password"
                            />
                            <label htmlFor="rp-confirm" className="mt-5 mb-2 block text-xs font-extrabold uppercase tracking-[0.2em] text-[#FDFBF7]/70">
                                Confirm Password
                            </label>
                            <input
                                id="rp-confirm"
                                data-testid="reset-confirm"
                                type="password"
                                required
                                value={confirm}
                                onChange={(e) => setConfirm(e.target.value)}
                                className="input-field"
                                placeholder="Repeat your new password"
                                autoComplete="new-password"
                            />
                            {error && (
                                <p data-testid="reset-error" role="alert" className="mt-4 text-sm font-semibold text-[#E8A0A0]">
                                    {error}
                                </p>
                            )}
                            <button type="submit" data-testid="reset-submit" disabled={busy} className="btn-gold mt-7 w-full disabled:cursor-not-allowed disabled:opacity-60">
                                {busy ? "Updating…" : "Update Password"}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </main>
    );
};

export default ResetPassword;
